import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import API from "../api";
import { useNavigate } from "react-router-dom";
import { Form, Button, Table } from "react-bootstrap";

export default function AdminEnterScoresPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [weeks, setWeeks] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState("");
  const [matchups, setMatchups] = useState([]);
  const [holes, setHoles] = useState([]);
  const [scoreByHole, setScoreByHole] = useState(false);
  const [scores, setScores] = useState([]);
  const inputRefs = React.useRef([]);

  useEffect(() => {
    if (user?.leagueid) {
      fetchWeeks();
    }
  }, [user]);

  const fetchWeeks = async () => {
    try {
      const res = await API.get(`/league/${user.leagueid}/weeks`);
      setWeeks(res.data);
    } catch (err) {
      console.error("Failed to fetch weeks", err);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      const index = inputRefs.current.indexOf(e.target);
      if (index > -1 && index < inputRefs.current.length - 1) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  const fetchMatchups = async (weekId) => {
    try {
      inputRefs.current = [];
      const res = await API.get(`/scores/${user.leagueid}/${weekId}`);
      setMatchups(res.data.matchups);
      setHoles(res.data.holes || []);
      setScoreByHole(res.data.score_by_hole);
      initializeScores(res.data.matchups, res.data.holes || [], res.data.existing_scores || [], weekId);
    } catch (err) {
      console.error("Failed to fetch matchups", err);
    }
  };

  const initializeScores = (matchups, holes, existingScores = [], weekId) => {
    const initialScores = [];

    matchups.forEach(match => {
      match.players.forEach(player => {
        if (scoreByHole) {
          holes.forEach(hole => {
            const existing = existingScores.find(
              (s) =>
                s.player_id === player.player_id &&
                s.hole_number === hole.hole_number
            );
            initialScores.push({
              player_id: player.player_id,
              week_id: weekId,
              hole_number: hole.hole_number,
              score: existing ? existing.score : ""
            });
          });
        }

        const existingTotal = existingScores.find(
          (s) =>
            s.player_id === player.player_id &&
            s.hole_number === null
        );
        initialScores.push({
          player_id: player.player_id,
          week_id: weekId,
          hole_number: null,
          score: existingTotal ? existingTotal.score : ""
        });
      });
    });

    setScores(initialScores);
  };

  const handleScoreChange = (playerId, holeNumber, value) => {
    setScores(prevScores => {
      const updated = prevScores.map(s => {
        if (s.player_id === playerId && s.hole_number === holeNumber) {
          return { ...s, score: value };
        }
        return s;
      });

      const exists = updated.some(s => s.player_id === playerId && s.hole_number === holeNumber);
      if (!exists) {
        updated.push({
          player_id: playerId,
          week_id: selectedWeek,
          hole_number: holeNumber,
          score: value
        });
      }

      if (scoreByHole) {
        // Recalculate total
        const totalScore = updated
          .filter(s => s.player_id === playerId && s.hole_number !== null)
          .reduce((sum, s) => sum + (parseInt(s.score) || 0), 0);

        const updatedWithTotal = updated.map(s => {
          if (s.player_id === playerId && s.hole_number === null) {
            return { ...s, score: totalScore };
          }
          return s;
        });

        return updatedWithTotal;
      }

      return updated;
    });
  };

  const handleSubmit = async () => {
    try {
      const filteredScores = [];

      const playerIds = [...new Set(scores.map(s => s.player_id))];

      playerIds.forEach(playerId => {
        const playerScores = scores.filter(s => s.player_id === playerId);

        const hasScore = playerScores.some(s => s.score !== "" && s.score !== null);

        if (hasScore) {
          filteredScores.push(...playerScores);
        }
      });

      if (filteredScores.length === 0) {
        alert("No scores to submit.");
        return;
      }

      await API.post("/scores/submit", {
        league_id: user.leagueid,
        week_id: selectedWeek,
        scores: filteredScores
      });

      alert("Scores saved successfully!");
      navigate("/dashboard");
    } catch (err) {
      console.error("Failed to submit scores", err);
      alert("Failed to submit scores.");
    }
  };

  return (
    <div className="container mt-4">
      <h3>Enter Scores</h3>

      <Form.Group className="mb-3">
        <Form.Label>Select Week</Form.Label>
        <Form.Select value={selectedWeek} onChange={(e) => {
          setSelectedWeek(e.target.value);
          fetchMatchups(e.target.value);
        }}>
          <option value="">Select a week...</option>
          {weeks.map(week => (
            <option key={week.id} value={week.id}>
              Week {week.week_number} - {week.date}
            </option>
          ))}
        </Form.Select>
      </Form.Group>

      {selectedWeek && (
        <>
          <Table bordered striped responsive>
            <thead>
              <tr>
                <th>Player</th>
                {scoreByHole ? (
                  <>
                    {holes.map(hole => (
                      <th key={hole.hole_number}>Hole {hole.hole_number}</th>
                    ))}
                    <th style={{ backgroundColor: "#ffdddd", width: "100px" }}>Total</th>
                  </>
                ) : (
                  <th style={{ width: "120px" }}>Total Score</th>
                )}
              </tr>
            </thead>
            <tbody>
              {matchups.map(match =>
                match.players.map(player => (
                  <tr key={`${match.match_id}-${player.player_id}`}>
                    <td>{player.player_name}</td>
                    {scoreByHole ? (
                      <>
                        {holes.map(hole => {
                          const scoreObj = scores.find(s =>
                            s.player_id === player.player_id &&
                            s.hole_number === hole.hole_number
                          );
                          return (
                            <td key={hole.hole_number}>
                              <Form.Control
                                type="number"
                                ref={(el) => {
                                  if (el) inputRefs.current.push(el);
                                }}
                                value={scoreObj?.score || ""}
                                onChange={(e) =>
                                  handleScoreChange(player.player_id, hole.hole_number, e.target.value)
                                }
                                onKeyDown={handleKeyDown}
                                min="0"
                              />
                            </td>
                          );
                        })}
                        <td style={{ backgroundColor: "#ffdddd", fontWeight: "bold" }}>
                          {scores.find(s => s.player_id === player.player_id && s.hole_number === null)?.score || 0}
                        </td>
                      </>
                    ) : (
                      <td style={{ width: "120px" }}>
                        <Form.Control
                          type="number"
                          ref={(el) => {
                            if (el) inputRefs.current.push(el);
                          }}
                          value={scores.find(s =>
                            s.player_id === player.player_id &&
                            s.hole_number === null
                          )?.score || ""}
                          onChange={(e) =>
                            handleScoreChange(player.player_id, null, e.target.value)
                          }
                          onKeyDown={handleKeyDown}
                          min="0"
                          className="w-100"
                        />
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          <Button variant="primary" onClick={handleSubmit}>
            Save Scores
          </Button>
        </>
      )}
    </div>
  );
}

